import { useEffect, useState } from 'react';
import { DragDropContext, DropResult, Droppable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';

import { AlignContainer, BaseSpinner } from 'wbs-components-lib';

import { Box, Typography } from '@mui/material';

import { BetEventModel } from '@shared/types';
import { reorder } from '@shared/utils';

import { useGetAllBetEventsQuery, useUpdateBetEventsOrderMutation } from '@services';

import { AppDispatch, getBetEventsSelector, setBetEvents } from '@store';

import { BetSystemEventItem } from './BetSystemEventItem';

import styles from './BetSystemEventsList.module.css';

const { betEventsList, betEventsListContainer } = styles;

export const BetSystemEventsList = () => {
  const dispatch = useDispatch<AppDispatch>();

  const betEvents = useSelector(getBetEventsSelector);

  const [expandedEventOutcomesId, setExpandedEventOutcomesId] = useState<string>('');

  const loadedBetEvents = useSelector(getBetEventsSelector);

  const { data: allBetEvents, isLoading: isBetEventsLoading, isSuccess: isBetEventsLoaded } = useGetAllBetEventsQuery();

  const [updateBetEventsOrder] = useUpdateBetEventsOrderMutation();

  useEffect(() => {
    if (isBetEventsLoaded) {
      dispatch(setBetEvents(allBetEvents));
    }
  }, [isBetEventsLoaded, allBetEvents]);

  const handleBetEventOutcomesExpand = (eventId: string) => {
    setExpandedEventOutcomesId(eventId);
  };

  const handleEventsOrder = async (reorderedEvents: BetEventModel[]) => {
    const orders = reorderedEvents.map((item) => {
      const { id, order } = item;

      return { id, order };
    });

    await updateBetEventsOrder({ orders });
  };

  const onDragEnd = async (result: DropResult) => {
    const { destination, source } = result;

    if (destination === null) {
      return;
    }

    const { index: destinationIndex } = destination!;
    const { index: sourceIndex } = source;

    if (destinationIndex === sourceIndex) {
      return;
    }

    const reorderedEvents = reorder<BetEventModel>(loadedBetEvents, sourceIndex, destinationIndex);

    dispatch(setBetEvents(reorderedEvents));

    await handleEventsOrder(reorderedEvents);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided) => (
          <Box className={betEventsList} ref={provided.innerRef} {...provided.droppableProps}>
            {!isBetEventsLoading ? (
              <>
                {betEvents.length !== 0 ? (
                  <Box className={betEventsListContainer}>
                    {betEvents.map((betEvent, index) => (
                      <BetSystemEventItem
                        index={index}
                        betEvent={betEvent}
                        handleBetEventOutcomesExpand={handleBetEventOutcomesExpand}
                        expandedEventOutcomesId={expandedEventOutcomesId}
                      />
                    ))}
                    {provided.placeholder}
                  </Box>
                ) : (
                  <Typography align="center" margin="50px">
                    There are no events at the moment. Please add a first one! 🤗
                  </Typography>
                )}
              </>
            ) : (
              <AlignContainer>
                <BaseSpinner />
              </AlignContainer>
            )}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};
